import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, CardHeader,
  FormControl, FormHelperText, FormLabel, HStack, Heading, Input, Radio,
  RadioGroup, Select, Spinner, Stack, Switch, Text, Textarea, VStack, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import bookingEngineService from '../services/bookingEngineService';
import cardGuaranteeService from '../services/cardGuaranteeService';

// Friendly labels (i18n keys) for the gateway credential fields the API lists.
const FIELD_LABELS = {
  stripe_publishable_key: 'bookingEnginePayments.fields.publishableKey',
  stripe_secret_key: 'bookingEnginePayments.fields.secretKey',
  razorpay_key_id: 'bookingEnginePayments.fields.keyId',
  razorpay_key_secret: 'bookingEnginePayments.fields.keySecret',
  razorpay_webhook_secret: 'bookingEnginePayments.fields.webhookSecret',
  paypal_client_id: 'bookingEnginePayments.fields.clientId',
  paypal_client_secret: 'bookingEnginePayments.fields.clientSecret',
  square_application_id: 'bookingEnginePayments.fields.applicationId',
  square_access_token: 'bookingEnginePayments.fields.accessToken',
  paymongo_public_key: 'bookingEnginePayments.fields.publicKey',
  paymongo_secret_key: 'bookingEnginePayments.fields.secretKey',
  paymongo_webhook_secret: 'bookingEnginePayments.fields.webhookSecret',
  xendit_secret_key: 'bookingEnginePayments.fields.secretKey',
  xendit_webhook_token: 'bookingEnginePayments.fields.webhookToken',
  midtrans_client_key: 'bookingEnginePayments.fields.clientKey',
  midtrans_server_key: 'bookingEnginePayments.fields.serverKey',
  midtrans_is_production: 'bookingEnginePayments.fields.productionMode',
};

// Query flag Stripe brings the owner back with (we put it on the redirect
// URL ourselves); its presence means "re-check the connection now".
const STRIPE_RETURN_FLAG = 'stripe_return';

/**
 * Card guarantee on OTA bookings: the hotel connects its OWN Stripe account
 * (via a Channex-issued OAuth link) so the desk can charge the card an OTA
 * guest gave. No keys are typed here — the whole flow is one button.
 */
const CardGuaranteeCard = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setStatus(await cardGuaranteeService.getStatus());
    } catch {
      setStatus({ available: false, reason: 'load_failed' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(STRIPE_RETURN_FLAG)) {
      // Back from Stripe: ask Channex which provider got connected, then
      // drop the flag so a reload does not re-trigger this.
      params.delete(STRIPE_RETURN_FLAG);
      const clean = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState({}, '', clean);
      (async () => {
        setLoading(true);
        try {
          const res = await cardGuaranteeService.refresh();
          setStatus(res);
          toast({
            title: res?.connected
              ? t('bookingEnginePayments.cardGuarantee.nowConnected')
              : t('bookingEnginePayments.cardGuarantee.stillNotConnected'),
            status: res?.connected ? 'success' : 'info',
            duration: 6000, isClosable: true,
          });
        } catch {
          await load();
        } finally {
          setLoading(false);
        }
      })();
    } else {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      setBusy(true);
      const url = new URL(window.location.href);
      url.searchParams.set(STRIPE_RETURN_FLAG, '1');
      const res = await cardGuaranteeService.connect(url.toString());
      if (res?.link) {
        window.location.assign(res.link);
        return;
      }
      throw new Error('no link');
    } catch (e) {
      toast({
        title: t('bookingEnginePayments.cardGuarantee.connectFailed'),
        description: e?.response?.data?.detail || e?.message,
        status: 'error', duration: 6000, isClosable: true,
      });
      setBusy(false);
    }
  };

  const refresh = async () => {
    try {
      setBusy(true);
      setStatus(await cardGuaranteeService.refresh());
      toast({ title: t('bookingEnginePayments.cardGuarantee.refreshed'), status: 'success', duration: 2500 });
    } catch (e) {
      toast({
        title: t('bookingEnginePayments.cardGuarantee.connectFailed'),
        description: e?.response?.data?.detail || e?.message,
        status: 'error', duration: 6000, isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  const connected = Boolean(status?.connected);
  const connectedAt = status?.connectedAt ? new Date(status.connectedAt).toLocaleDateString() : '';

  return (
    <Card>
      <CardHeader pb={0}>
        <HStack justify="space-between" align="start">
          <Box>
            <Heading size="sm">{t('bookingEnginePayments.cardGuarantee.title')}</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {t('bookingEnginePayments.cardGuarantee.subtitle')}
            </Text>
          </Box>
          {!loading && status?.available && (
            <Badge colorScheme={connected ? 'green' : 'yellow'} whiteSpace="nowrap">
              {connected
                ? t('bookingEnginePayments.cardGuarantee.statusConnected')
                : t('bookingEnginePayments.cardGuarantee.statusNotConnected')}
            </Badge>
          )}
        </HStack>
      </CardHeader>
      <CardBody>
        {loading ? (
          <HStack py={4} justify="center"><Spinner size="sm" /></HStack>
        ) : !status?.available ? (
          <Alert status="info" borderRadius="md" fontSize="sm">
            <AlertIcon />
            {t('bookingEnginePayments.cardGuarantee.notConfigured')}
          </Alert>
        ) : (
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" color="gray.600">{t('bookingEnginePayments.cardGuarantee.how')}</Text>
            {connected && (
              <Text fontSize="sm">
                {status.providerTitle ? `${status.providerTitle} · ` : ''}
                {status.providerAccount
                  ? t('bookingEnginePayments.cardGuarantee.account', { account: status.providerAccount })
                  : ''}
                {connectedAt ? ` · ${t('bookingEnginePayments.cardGuarantee.connectedAt', { date: connectedAt })}` : ''}
              </Text>
            )}
            <HStack spacing={3} flexWrap="wrap">
              <Button colorScheme={connected ? 'gray' : 'purple'} variant={connected ? 'outline' : 'solid'}
                onClick={connect} isLoading={busy}>
                {connected
                  ? t('bookingEnginePayments.cardGuarantee.reconnect')
                  : t('bookingEnginePayments.cardGuarantee.connect')}
              </Button>
              <Button variant="ghost" onClick={refresh} isDisabled={busy}>
                {t('bookingEnginePayments.cardGuarantee.refresh')}
              </Button>
            </HStack>
            {!connected && (
              <Text fontSize="xs" color="gray.500">{t('bookingEnginePayments.cardGuarantee.connectHelp')}</Text>
            )}
            <Text fontSize="xs" color="gray.500">{t('bookingEnginePayments.cardGuarantee.feesNote')}</Text>
            <Text fontSize="xs" color="gray.500">{t('bookingEnginePayments.cardGuarantee.policyNote')}</Text>
          </VStack>
        )}
      </CardBody>
    </Card>
  );
};

const BookingEnginePayments = () => {
  const { t } = useTranslation();
  const fieldLabel = (key) => (FIELD_LABELS[key] ? t(FIELD_LABELS[key]) : key);
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [requirePayment, setRequirePayment] = useState(false);
  const [gateway, setGateway] = useState('none');
  const [fields, setFields] = useState({});
  const [guaranteeMode, setGuaranteeMode] = useState('off');
  const [guaranteePolicy, setGuaranteePolicy] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await bookingEngineService.getPaymentSettings();
      setData(res);
      setRequirePayment(!!res.requirePayment);
      setGateway(res.paymentGateway || 'none');
      setGuaranteeMode(res.cardGuaranteeMode || 'off');
      setGuaranteePolicy(res.cardGuaranteePolicyText || '');
      const seeded = {};
      Object.values(res.gateways || {}).forEach((spec) => {
        Object.entries(spec.fields || {}).forEach(([key, value]) => { seeded[key] = value; });
      });
      setFields(seeded);
      setError('');
    } catch {
      setError(t('bookingEnginePayments.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const spec = useMemo(
    () => (gateway !== 'none' ? (data?.gateways || {})[gateway] : null),
    [data, gateway],
  );

  const setField = (key) => (e) => {
    const value = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    try {
      setSaving(true);
      const payload = {
        requirePayment,
        paymentGateway: gateway,
        // Guarantee is Stripe-only; switching gateway away turns it off.
        cardGuaranteeMode: gateway === 'stripe' ? guaranteeMode : 'off',
        cardGuaranteePolicyText: guaranteePolicy,
        fields: {},
      };
      if (spec) {
        [...(spec.fields ? Object.keys(spec.fields) : [])].forEach((key) => {
          payload.fields[key] = fields[key];
        });
        Object.keys(spec.secrets || {}).forEach((key) => {
          if (fields[key]) payload.fields[key] = fields[key]; // blank = keep stored
        });
      }
      const res = await bookingEngineService.updatePaymentSettings(payload);
      setData(res);
      // Secrets never round-trip — drop what was typed now that it's saved.
      setFields((prev) => {
        const next = { ...prev };
        Object.keys(spec?.secrets || {}).forEach((key) => { delete next[key]; });
        return next;
      });
      toast({ title: t('bookingEnginePayments.toasts.saved'), status: 'success', duration: 2500 });
    } catch (e) {
      toast({
        title: t('bookingEnginePayments.toasts.saveFailed'),
        description: e?.response?.data?.detail || t('bookingEnginePayments.toasts.checkFields'),
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageShell title={t('bookingEnginePayments.page.title')}>
        <HStack py={10} justify="center"><Spinner /></HStack>
      </PageShell>
    );
  }
  if (error) {
    return (
      <PageShell title={t('bookingEnginePayments.page.title')}>
        <Alert status="error"><AlertIcon />{error}</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={t('bookingEnginePayments.page.title')}
      subtitle={t('bookingEnginePayments.page.subtitle')}
    >
      <VStack align="stretch" spacing={4} maxW="760px">
        <Card>
          <CardHeader pb={0}><Heading size="sm">{t('bookingEnginePayments.mode.title')}</Heading></CardHeader>
          <CardBody>
            <RadioGroup
              value={requirePayment ? 'charge' : 'none'}
              onChange={(v) => setRequirePayment(v === 'charge')}
            >
              <Stack spacing={3}>
                <Radio value="none">
                  <Box>
                    <Text fontWeight="600">{t('bookingEnginePayments.mode.none')}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {t('bookingEnginePayments.mode.noneHelp')}
                    </Text>
                  </Box>
                </Radio>
                <Radio value="charge">
                  <Box>
                    <Text fontWeight="600">{t('bookingEnginePayments.mode.charge')}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {t('bookingEnginePayments.mode.chargeHelp')}
                    </Text>
                  </Box>
                </Radio>
              </Stack>
            </RadioGroup>
          </CardBody>
        </Card>

        <Card>
          <CardHeader pb={0}>
            <HStack justify="space-between">
              <Heading size="sm">{t('bookingEnginePayments.gateway.title')}</Heading>
              {!requirePayment && <Badge>{t('bookingEnginePayments.gateway.optionalBadge')}</Badge>}
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <FormControl maxW="320px">
                <FormLabel fontSize="sm">{t('bookingEnginePayments.gateway.label')}</FormLabel>
                <Select size="sm" value={gateway} onChange={(e) => setGateway(e.target.value)}>
                  <option value="none">{t('bookingEnginePayments.gateway.none')}</option>
                  {Object.entries(data?.gateways || {}).map(([key, g]) => (
                    <option key={key} value={key}>{g.label}</option>
                  ))}
                </Select>
                <FormHelperText>
                  {t('bookingEnginePayments.gateway.help')}
                </FormHelperText>
              </FormControl>

              {spec && (
                <VStack align="stretch" spacing={3}>
                  {Object.entries(spec.fields || {}).map(([key, value]) => (
                    typeof value === 'boolean' || typeof fields[key] === 'boolean' ? (
                      <FormControl key={key} display="flex" alignItems="center" gap={3}>
                        <Switch isChecked={!!fields[key]} onChange={setField(key)} />
                        <FormLabel mb={0} fontSize="sm">{fieldLabel(key)}</FormLabel>
                      </FormControl>
                    ) : (
                      <FormControl key={key}>
                        <FormLabel fontSize="sm">{fieldLabel(key)}</FormLabel>
                        <Input size="sm" value={fields[key] ?? ''} onChange={setField(key)} />
                      </FormControl>
                    )
                  ))}
                  {Object.entries(spec.secrets || {}).map(([key, isSet]) => (
                    <FormControl key={key}>
                      <FormLabel fontSize="sm">{fieldLabel(key)}</FormLabel>
                      <Input
                        size="sm"
                        type="password"
                        autoComplete="new-password"
                        value={fields[key] ?? ''}
                        onChange={setField(key)}
                        placeholder={isSet ? t('bookingEnginePayments.gateway.secretStored') : t('bookingEnginePayments.gateway.secretNotSet')}
                      />
                    </FormControl>
                  ))}
                </VStack>
              )}
            </VStack>
          </CardBody>
        </Card>

        {gateway === 'stripe' && (
          <Card>
            <CardHeader pb={0}>
              <HStack justify="space-between">
                <Heading size="sm">{t('bookingEnginePayments.directGuarantee.title')}</Heading>
                <Badge colorScheme={guaranteeMode === 'off' ? 'gray' : 'green'}>
                  {t(`bookingEnginePayments.directGuarantee.mode_${guaranteeMode}`)}
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Text fontSize="sm" color="gray.600">
                  {t('bookingEnginePayments.directGuarantee.help')}
                </Text>
                <FormControl maxW="320px">
                  <FormLabel fontSize="sm">{t('bookingEnginePayments.directGuarantee.modeLabel')}</FormLabel>
                  <Select size="sm" value={guaranteeMode} onChange={(e) => setGuaranteeMode(e.target.value)}>
                    <option value="off">{t('bookingEnginePayments.directGuarantee.mode_off')}</option>
                    <option value="optional">{t('bookingEnginePayments.directGuarantee.mode_optional')}</option>
                    <option value="required">{t('bookingEnginePayments.directGuarantee.mode_required')}</option>
                  </Select>
                </FormControl>
                {guaranteeMode !== 'off' && (
                  <FormControl>
                    <FormLabel fontSize="sm">{t('bookingEnginePayments.directGuarantee.policyLabel')}</FormLabel>
                    <Textarea
                      size="sm"
                      rows={3}
                      value={guaranteePolicy}
                      onChange={(e) => setGuaranteePolicy(e.target.value)}
                      placeholder={t('bookingEnginePayments.directGuarantee.policyPlaceholder')}
                    />
                    <FormHelperText>{t('bookingEnginePayments.directGuarantee.policyHelp')}</FormHelperText>
                  </FormControl>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        <HStack justify="flex-end">
          <Button colorScheme="blue" onClick={save} isLoading={saving}>{t('bookingEnginePayments.buttons.save')}</Button>
        </HStack>

        <CardGuaranteeCard />
      </VStack>
    </PageShell>
  );
};

export default BookingEnginePayments;
